import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Tag, Brain, Lightbulb, BookOpen, Users } from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';

const categories = [
  { name: 'All', slug: 'all' },
  { name: 'AI & Technology', slug: 'ai', icon: Brain },
  { name: 'Entrepreneurship', slug: 'entrepreneurship', icon: Lightbulb },
  { name: 'Publishing', slug: 'publishing', icon: BookOpen },
  { name: 'Philosophy', slug: 'philosophy', icon: Users }
];

const insights = [
  {
    id: 'ai-survival-guide-humans',
    title: 'Embracing the AI Revolution: A Human\'s Guide to Thriving',
    excerpt: 'What if AI, instead of being something to fear, is actually a powerful tool that can enhance our creativity, productivity, and quality of life? A fresh perspective on human-AI collaboration.',
    category: 'AI & Technology',
    categorySlug: 'ai',
    date: 'July 2024',
    readTime: '14 min read',
    featured: true,
    color: '#3B82F6',
    hasFullArticle: true
  },
  {
    id: 'use-what-you-got-philosophy',
    title: 'Unlocking Your Potential: The "Use What You Got" Philosophy',
    excerpt: 'Success isn\'t about waiting for perfect circumstances or resources—it\'s about leveraging what you already have. A deep dive into the philosophy of resourcefulness and determination.',
    category: 'Philosophy',
    categorySlug: 'philosophy',
    date: 'July 2024',
    readTime: '15 min read',
    featured: true,
    color: '#D4AF37',
    hasFullArticle: true
  },
  {
    id: 'restaurant-success-guide',
    title: 'Cooking Up Success: The Beginner\'s Guide to Restaurant Success',
    excerpt: 'In the dynamic world of culinary entrepreneurship, having a reliable roadmap makes all the difference. Practical wisdom from Chef Robert "BB" Brunet for aspiring restaurateurs.',
    category: 'Entrepreneurship',
    categorySlug: 'entrepreneurship',
    date: 'July 2024',
    readTime: '12 min read',
    featured: false,
    color: '#F59E0B',
    hasFullArticle: true
  },
  {
    id: 'ai-survival-guide-small-business',
    title: 'Unlocking the Power of AI for Small Businesses',
    excerpt: 'AI is no longer exclusive to tech giants. Learn how small businesses can leverage artificial intelligence to level the playing field, enhance efficiency, and drive growth.',
    category: 'AI & Technology',
    categorySlug: 'ai',
    date: 'July 2024',
    readTime: '11 min read',
    featured: false,
    color: '#10B981',
    hasFullArticle: true
  },
  {
    id: 'digital-legacy-preservation',
    title: 'Digital Legacy: Preserving Human Stories in an AI World',
    excerpt: 'Our ancestors left cave paintings. What will we leave? Exploring the intersection of technology and human memory, and why digital legacy preservation matters.',
    category: 'Philosophy',
    categorySlug: 'philosophy',
    date: 'January 2026',
    readTime: '10 min read',
    featured: false,
    color: '#8B5CF6',
    hasFullArticle: false
  },
  {
    id: 'nfq-never-quit',
    title: 'NFQ: The Mindset Behind Never Giving Up',
    excerpt: 'Perseverance isn\'t stubbornness—it\'s intelligent persistence. How to know when to push through and when to pivot, and why the journey matters as much as the destination.',
    category: 'Philosophy',
    categorySlug: 'philosophy',
    date: 'October 2025',
    readTime: '9 min read',
    featured: false,
    color: '#EF4444',
    hasFullArticle: false
  },
  {
    id: 'systems-thinking-technology',
    title: 'Systems Thinking for Technology Leaders',
    excerpt: 'How to see the patterns behind complex problems, identify leverage points, and design solutions that address root causes rather than symptoms.',
    category: 'AI & Technology',
    categorySlug: 'ai',
    date: 'September 2025',
    readTime: '11 min read',
    featured: false,
    color: '#3B82F6',
    hasFullArticle: false
  },
  {
    id: 'building-ventures-that-matter',
    title: 'Building Ventures That Matter',
    excerpt: 'Beyond profit: how to build businesses that create real value for customers, communities, and the broader world. Lessons from decades of entrepreneurship.',
    category: 'Entrepreneurship',
    categorySlug: 'entrepreneurship',
    date: 'August 2025',
    readTime: '13 min read',
    featured: false,
    color: '#D4AF37',
    hasFullArticle: false
  }
];

export default function InsightsPage() {
  const featuredInsights = insights.filter(i => i.featured);
  const recentInsights = insights.filter(i => !i.featured);

  return (
    <main className="pt-20">
      {/* Hero */}
      <Section>
        <div className="max-w-4xl">
          <motion.span
            className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Insights
          </motion.span>
          <motion.h1
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="insights-headline"
          >
            Essays & Thought Leadership
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Explorations of artificial intelligence, entrepreneurship, publishing, and the philosophies that guide meaningful work. Ideas worth sharing.
          </motion.p>
        </div>
      </Section>

      {/* Category Filter */}
      <Section dark className="py-8">
        <div className="flex flex-wrap gap-3">
          {categories.map((cat, index) => (
            <motion.button
              key={cat.slug}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                index === 0 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card border border-border text-muted-foreground hover:text-white hover:border-primary/50'
              }`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              data-testid={`category-${cat.slug}`}
            >
              {cat.name}
            </motion.button>
          ))}
        </div>
      </Section>

      {/* Featured Insights */}
      <Section>
        <SectionTitle subtitle="Featured">Essential Reading</SectionTitle>
        
        <div className="grid md:grid-cols-2 gap-8">
          {featuredInsights.map((insight, index) => (
            <Link 
              key={insight.id}
              to={insight.hasFullArticle ? `/insights/${insight.id}` : '#'}
              className="block"
            >
              <motion.article
                className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-colors h-full"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4 }}
              >
                <div 
                  className="h-48 relative"
                  style={{ backgroundColor: insight.color }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                  <div className="absolute bottom-4 left-6">
                    <span className="text-xs px-3 py-1 rounded-full bg-white/20 text-white backdrop-blur-sm">
                      {insight.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {insight.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {insight.readTime}
                    </span>
                  </div>
                  
                  <h2 className="text-xl font-bold text-white mb-3 group-hover:text-primary transition-colors">
                    {insight.title}
                  </h2>
                  
                  <p className="text-muted-foreground mb-4 line-clamp-3">
                    {insight.excerpt}
                  </p>
                  
                  <span className="text-primary text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    {insight.hasFullArticle ? 'Read Essay' : 'Coming Soon'} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </motion.article>
            </Link>
          ))}
        </div>
      </Section>

      {/* All Insights */}
      <Section dark>
        <SectionTitle subtitle="Archive">All Insights</SectionTitle>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recentInsights.map((insight, index) => (
            <Link 
              key={insight.id}
              to={insight.hasFullArticle ? `/insights/${insight.id}` : '#'}
              className="block"
            >
              <AnimatedCard delay={index * 0.05} className="group h-full">
                <div className="flex items-center gap-2 mb-3">
                  <span 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: insight.color }}
                  />
                  <span className="text-xs text-muted-foreground uppercase tracking-widest">
                    {insight.category}
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                  {insight.title}
                </h3>
                
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {insight.excerpt}
                </p>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{insight.date}</span>
                  <span>{insight.hasFullArticle ? insight.readTime : 'Coming Soon'}</span>
                </div>
              </AnimatedCard>
            </Link>
          ))}
        </div>
      </Section>

      {/* Newsletter CTA */}
      <Section>
        <div className="bg-card border border-border rounded-2xl p-8 md:p-12 text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Stay Updated
            </h2>
            <p className="text-muted-foreground mb-6">
              New essays on AI, entrepreneurship, and the future of technology. Delivered when something is worth sharing—no spam, no fluff.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 h-12 px-4 rounded-lg bg-input border border-border focus:border-primary focus:ring-1 focus:ring-primary text-foreground"
                data-testid="newsletter-email"
              />
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6" data-testid="newsletter-subscribe">
                Subscribe
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* CTA */}
      <Section dark>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Let's Discuss Ideas
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Have thoughts on these topics? Interested in speaking or collaboration opportunities? I welcome meaningful conversations.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="contact-btn">
              <Link to="/contact">Start a Conversation</Link>
            </Button>
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="speaking-btn">
              <Link to="/speaking">Speaking Inquiries <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </Section>
    </main>
  );
}
